import { AdvancedSelect, compactSelectStyles } from '../../../../../widgets/select';
import { BasicButton, Link } from '../../../../../widgets/buttons';
import { useDialogManager } from '../../../../../widgets/dialog';
import { Icon } from '../../../../../widgets/icon';
import { CompactInput } from '../../../../../widgets/input';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import Stack from '../../../../../widgets/stack/stack';
import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { tryOnTypeOptions } from 'src/components/pages/virtualTryOn/hooks/useVTOProductType';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { TryOnProvider, TryOnSettingsType, TryOnType } from 'src/components/widgets/preview3D/tryOnTypes';
import { T, toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import RestartAltIcon from 'src/shared/material_icons/device/restart_alt.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import { PropertiesPanelColorControl } from '../scene-properties-panel/propertiesPanelColorProperty';
import {
    SettingControl,
    SettingsFieldLabel,
    SettingsFieldRow,
    SettingsGroup,
    SettingsGroupSwitch,
    SettingsMessage,
    SettingsToggleRow
} from '../sceneEditorSettingsLayout';
import { ButtonPreview, IconImage, IconImageContainer, IconPlaceholder, PreviewIcon } from './vtoEditor.styles';
import { EditorDialog } from '../editorDialogWindow';

const LoadingState = styled.p`
    margin: 0;
    color: var(--editor-faint, #676a6e);
    font-size: 12px;
`;

type ButtonCustomization = {
    iconImage: string;
    textLabel: string;
    textColor: string;
    backgroundColor: string;
    borderColor: string;
    borderSize: string;
    borderRadius: string;
};

type ButtonColorKey = 'textColor' | 'backgroundColor' | 'borderColor';

const toPixelOptions = (from: number) => Array.from({ length: 11 - from }, (_, index) => `${index + from}px`).map((value) => ({ value, label: value }));
const BORDER_SIZE_OPTIONS = toPixelOptions(1);
const BORDER_RADIUS_OPTIONS = toPixelOptions(0);

// Un bottone icona resta quadrato, alto quanto la riga.
const IconActionButton = styled(BasicButton)`
    &&& {
        width: ${(props) => props.theme.app.component.button.small.height};
    }
`;

const IconAction: FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
    <IconActionButton type='button' $type='icon' $size='small' aria-label={label} onClick={onClick}>
        <Icon tooltip={label}>{children}</Icon>
    </IconActionButton>
);

const TooltipLink = styled.a`
    color: ${(props) => props.theme.app.component.link.color};
    text-decoration: underline;
`;

const ScalingMessage = styled(Stack)`
    margin: 8px 0;
    line-height: 1.5;
`;

// IL MENU ESCE DALLA PAGINA (come in propertiesPanelSelectProperty): la pagina delle Settings scorre e ritaglia ciò
// che sporge, e un elenco aperto in fondo alla card finirebbe tagliato. In fondo al documento, e in su se serve.
const compactSelectProps = {
    styles: compactSelectStyles as any,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPlacement: 'auto' as const
};

// I GRUPPI DELLA PAGINA, con lo stesso passo che hanno nelle altre pagine delle Settings.
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

// LE TENDINE DEI DUE BOTTONI SONO RIGHE DELLA CARD: il nome a sinistra, la freccia a destra, e il fondo solo al passaggio.
const DisclosureRow = styled.button<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: calc(100% + 12px);
    min-height: 24px;
    margin: 0 -6px;
    padding: 0 6px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--editor-muted, #55595f);
    font-family: inherit;
    font-size: 12px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: var(--editor-hover, #eff0f2);
    }

    svg {
        flex: none;
        width: 16px;
        height: 16px;
        fill: var(--editor-faint, #676a6e);
        transform: rotate(${(props) => (props.$isOpen ? '180deg' : '0deg')});
        transition: transform 0.2s;
    }
`;

// IL PANNELLO APERTO STA SOTTO LA SUA RIGA, un passo più in dentro, con un filo a sinistra che dice di chi è.
const CustomizationPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-left: 2px;
    padding: 2px 0 2px 12px;
    border-left: 1px solid var(--editor-line, #e3e5e8);
`;

const IconPreview = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 2px;
    border: 1px dashed var(--editor-line, #e3e5e8);
    border-radius: 6px;
    background: var(--editor-field, #ffffff);
    overflow: hidden;
`;

const TextLabelControl = styled(SettingControl)`
    display: flex;
    align-items: center;
    gap: 4px;
`;

// «MANDATORY» DIPENDE DALLA RIGA SOPRA, e ci sta sotto un passo più in dentro.
const DependentOption = styled.div`
    padding-left: 16px;
`;

// I COMANDI IN FILA, alla loro misura: se la colonna è stretta vanno a capo invece di uscire dal bordo.
const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

// LO STATO DEI MODELLI DI RIFERIMENTO È UNA RIGA DELLA CARD, non un banner del Backoffice: il pallino ne porta il colore.
type ReferenceTone = 'info' | 'success' | 'warning';

const ReferenceStatus = styled.p<{ $tone: ReferenceTone }>`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--editor-muted, #55595f);
    font-size: 12px;

    &::before {
        content: '';
        flex: none;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${(props) => {
            const banner = props.theme.app.component.notificationBanner;
            if (props.$tone === 'success') return banner.success.iconContainerBGColor;
            if (props.$tone === 'warning') return banner.warning.iconContainerBGColor;
            return banner.information.iconContainerBGColor;
        }};
    }
`;

interface ButtonCustomizationPanelProps {
    idPrefix: string;
    values: ButtonCustomization;
    /** La sigla al posto dell'icona, quando l'icona non c'è. */
    placeholder: string;
    iconAlt: string;
    onChange: (changes: Partial<ButtonCustomization>) => void;
    onIconError: () => void;
    onUploadIcon: () => void;
    onResetIcon: () => void;
    onDeleteIcon: () => void;
    onResetTextLabel: () => void;
}

// I DUE BOTTONI SI PERSONALIZZANO NELLO STESSO MODO — il try-on e, per gli occhiali, la distanza
// pupillare. Erano due copie dello stesso modulo, e ogni correzione andava fatta due volte.
const ButtonCustomizationPanel: FC<ButtonCustomizationPanelProps> = ({
    idPrefix,
    values,
    placeholder,
    iconAlt,
    onChange,
    onIconError,
    onUploadIcon,
    onResetIcon,
    onDeleteIcon,
    onResetTextLabel
}) => {
    const colorRows: { key: ButtonColorKey; label: string }[] = [
        { key: 'textColor', label: T._('Text color', 'Admin') },
        { key: 'backgroundColor', label: T._('Background color', 'Admin') },
        { key: 'borderColor', label: T._('Border color', 'Admin') }
    ];

    return (
        <CustomizationPanel>
            <SettingsFieldRow>
                <SettingsFieldLabel as='span'>{T._('Icon Image', 'Admin')}</SettingsFieldLabel>
                <IconImageContainer>
                    <IconPreview>
                        {values.iconImage ? (
                            <IconImage src={values.iconImage} alt={iconAlt} onError={onIconError} />
                        ) : (
                            <IconPlaceholder>{placeholder}</IconPlaceholder>
                        )}
                    </IconPreview>
                    <IconAction label={T._('Upload image', 'Admin')} onClick={onUploadIcon}>
                        <EditIcon />
                    </IconAction>
                    <IconAction label={T._('Reset icon', 'Admin')} onClick={onResetIcon}>
                        <RestartAltIcon />
                    </IconAction>
                    <IconAction label={T._('Delete icon', 'Admin')} onClick={onDeleteIcon}>
                        <DeleteIcon />
                    </IconAction>
                </IconImageContainer>
            </SettingsFieldRow>
            <SettingsFieldRow>
                <SettingsFieldLabel htmlFor={`${idPrefix}-text-label`}>{T._('Text label', 'Admin')}</SettingsFieldLabel>
                <TextLabelControl>
                    <CompactInput
                        id={`${idPrefix}-text-label`}
                        type='text'
                        value={values.textLabel}
                        onChange={(e) => onChange({ textLabel: e.target.value })}
                    />
                    <IconAction label={T._('Reset to default', 'Admin')} onClick={onResetTextLabel}>
                        <RestartAltIcon />
                    </IconAction>
                </TextLabelControl>
            </SettingsFieldRow>
            {colorRows.map(({ key, label }) => (
                <SettingsFieldRow key={key}>
                    <SettingsFieldLabel htmlFor={`${idPrefix}-${key}`}>{label}</SettingsFieldLabel>
                    <SettingControl>
                        <PropertiesPanelColorControl
                            id={`${idPrefix}-${key}`}
                            value={values[key]}
                            onChange={(color) => onChange({ [key]: color } as Partial<ButtonCustomization>)}
                        />
                    </SettingControl>
                </SettingsFieldRow>
            ))}
            <SettingsFieldRow>
                <SettingsFieldLabel as='span'>{T._('Border size', 'Admin')}</SettingsFieldLabel>
                <SettingControl>
                    <AdvancedSelect
                        aria-label={T._('Border size', 'Admin')}
                        options={BORDER_SIZE_OPTIONS}
                        value={BORDER_SIZE_OPTIONS.find((option) => option.value === values.borderSize) ?? null}
                        onChange={(option) => option && onChange({ borderSize: option.value })}
                        {...compactSelectProps}
                    />
                </SettingControl>
            </SettingsFieldRow>
            <SettingsFieldRow>
                <SettingsFieldLabel as='span'>{T._('Border radius', 'Admin')}</SettingsFieldLabel>
                <SettingControl>
                    <AdvancedSelect
                        aria-label={T._('Border radius', 'Admin')}
                        options={BORDER_RADIUS_OPTIONS}
                        value={BORDER_RADIUS_OPTIONS.find((option) => option.value === values.borderRadius) ?? null}
                        onChange={(option) => option && onChange({ borderRadius: option.value })}
                        {...compactSelectProps}
                    />
                </SettingControl>
            </SettingsFieldRow>
            <SettingsFieldRow>
                <SettingsFieldLabel as='span'>{T._('Preview', 'Admin')}</SettingsFieldLabel>
                <ButtonPreview
                    backgroundColor={values.backgroundColor}
                    textColor={values.textColor}
                    borderSize={values.borderSize}
                    borderColor={values.borderColor}
                    borderRadius={values.borderRadius}
                >
                    {values.iconImage && <PreviewIcon src={values.iconImage} alt={iconAlt} />}
                    <span>{values.textLabel}</span>
                </ButtonPreview>
            </SettingsFieldRow>
        </CustomizationPanel>
    );
};

// Tipi per le query GraphQL
type TryOnProviderQuery = {
    key: string;
    value: number;
};

type ProductTryOn = {
    id: string;
    productID: string;
    typeID: number;
    providerID: number;
    name: string;
    code: string;
    settings: string;
};

// Tipo per i settings parsati
type VTOSettings = {
    pd: any | null;
    try_on: boolean;
    active: boolean;
    mandatoryPD?: boolean;
    buttonCustomizations?: {
        iconImage?: string;
        textLabel?: string;
        textColor?: string;
        backgroundColor?: string;
        borderColor?: string;
        borderSize?: string;
        borderRadius?: string;
    };
};

// Query GraphQL
const VTO_PROVIDERS_QUERY = graphql`
    query vtoEditorProvidersQuery {
        tryOnProviders {
            list {
                key
                value
            }
        }
    }
`;

const PRODUCT_TRY_ON_QUERY = graphql`
    query vtoEditorProductTryOnQuery($productTryOnId: ID!) {
        productTryOn(id: $productTryOnId) {
            id
            productID
            typeID
            providerID
            name
            code
            settings
        }
    }
`;

const TRY_ON_CUSTOMER_QUERY = graphql`
    query vtoEditorTryOnCustomerQuery($typeId: Int!, $providerId: Long!) {
        tryOnCustomer(typeID: $typeId, providerID: $providerId) {
            id
            sellerID
            providerID
            typeID
            settings
            vtoState
        }
    }
`;

// CHI SA PROVARE QUESTO PRODOTTO. Il provider non è una preferenza: è il tipo di prodotto a
// deciderlo — occhiali e sciarpe li fa Mazing o Banuba, le scarpe DeepAR — e la mappatura
// tipo → provider arriva dal server. Il prodotto già salvato porta il proprio, e vale finché non
// si cambia tipo: cambiato quello, il provider salvato parla di un altro prodotto.
//
// È lo stesso ordine del vecchio backoffice, ed è il numero da cui dipende tutto il finetuning:
// quale cornice aprire (iframe per Mazing e Banuba, QR code per DeepAR) e se aprirne una.
const resolveTryOnProvider = (providersQuery: any, productTryOnQuery: any, selectedType: TryOnType | null, isVTOEnabled: boolean): TryOnProvider => {
    if (!isVTOEnabled || !selectedType) return TryOnProvider.NotSelected;

    const productTryOn = productTryOnQuery?.productTryOn;
    if (productTryOn && Number(productTryOn.typeID) === Number(selectedType) && productTryOn.providerID) {
        return productTryOn.providerID as TryOnProvider;
    }

    const mapped = providersQuery?.tryOnProviders?.list?.find((entry: TryOnProviderQuery) => Number(entry.key) === Number(selectedType))?.value;
    return (mapped as TryOnProvider) ?? TryOnProvider.NotSelected;
};

// Componente interno per gestire le query con dati
interface VTOEditorContentProps {
    productTryOnId: string;
    additionalFeatures?: any;
    /** La scena è cambiata: chi ci sta sopra segni che c’è da salvare. */
    onSceneChanged?: () => void;
}

const VTOEditorContent: React.FC<VTOEditorContentProps> = ({ productTryOnId, additionalFeatures, onSceneChanged }) => {
    const [isVTOEnabled, setIsVTOEnabled] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<TryOnType | null>(null);
    const [vtoSettings, setVtoSettings] = useState<VTOSettings | null>(null);

    // Stati per le opzioni specifiche Eyewear
    const [virtualTryOnEnabled, setVirtualTryOnEnabled] = useState(false);
    const [pupillaryDistanceEnabled, setPupillaryDistanceEnabled] = useState(false);
    const [mandatoryPDEnabled, setMandatoryPDEnabled] = useState(false);

    // Stati per l'Action Button panel
    const [isActionButtonPanelOpen, setIsActionButtonPanelOpen] = useState(false);

    // 🔧 NUOVO: Stati separati per Virtual Try-On (primo bottone)
    const [buttonCustomizations, setButtonCustomizations] = useState({
        iconImage: '',
        textLabel: 'Virtual Try-on',
        textColor: '#000000',
        backgroundColor: '#F4F4F4',
        borderColor: '#F4F4F4',
        borderSize: '1px',
        borderRadius: '0px'
    });

    // 🔧 NUOVO: Stati separati per Pupillary Distance (secondo bottone) - solo per Eyewear
    const [pupillaryDistanceCustomizations, setPupillaryDistanceCustomizations] = useState({
        iconImage: 'base',
        textLabel: 'Pupillary Distance',
        textColor: '#000000',
        backgroundColor: '#F4F4F4',
        borderColor: '#F4F4F4',
        borderSize: '1px',
        borderRadius: '0px'
    });

    // Pannelli separati per i due bottoni
    const [isVirtualTryOnPanelOpen, setIsVirtualTryOnPanelOpen] = useState(false);
    const [isPupillaryDistancePanelOpen, setIsPupillaryDistancePanelOpen] = useState(false);

    // 🔧 NUOVO: Stato per tenere traccia dei VTO reference files
    const [hasVTOReferenceModels, setHasVTOReferenceModels] = useState(false);
    const [isLoadingVTOReferences, setIsLoadingVTOReferences] = useState(false);

    // 🔧 NUOVO: Flag per evitare sincronizzazione durante l'inizializzazione
    const [isInitializing, setIsInitializing] = useState(true);

    // Hook per accedere al sceneEditor e dialog manager
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);
    const { showDialog, closeDialog } = useDialogManager();

    // Query per ottenere i provider e tipi disponibili
    const providersQuery = useLazyLoadQuery<any>(VTO_PROVIDERS_QUERY, {});

    // Query per ottenere le informazioni del prodotto Try-On
    // Arriva già come identificativo globale: lo compone chi conosce il prodotto.
    const productTryOnQuery = useLazyLoadQuery<any>(PRODUCT_TRY_ON_QUERY, { productTryOnId });

    // Funzione helper per determinare il valore di styleButton
    const updateStyleButton = React.useCallback(
        (vtoType: TryOnType | null, virtualTryOn: boolean, pupillaryDistance: boolean) => {
            const shouldUseSeparateButtons = vtoType === TryOnType.Eyewear && virtualTryOn && pupillaryDistance;
            const styleButtonValue: 'multiple' | 'single' = shouldUseSeparateButtons ? 'multiple' : 'single';

            console.log('🔧 UpdateStyleButton called with:', {
                vtoType,
                virtualTryOn,
                pupillaryDistance,
                shouldUseSeparateButtons,
                styleButtonValue
            });

            if (sceneEditor) {
                try {
                    const currentTryOnSettings = sceneEditor.getTryOnSettings();
                    if (currentTryOnSettings) {
                        console.log('🔍 Current tryOnSettings before update:', currentTryOnSettings);
                        const updatedSettings = {
                            ...currentTryOnSettings,
                            styleButton: styleButtonValue
                        };
                        sceneEditor.setTryOnTypeSettings(updatedSettings);
                        console.log('✅ StyleButton updated to:', styleButtonValue);

                        // Verifica che l'aggiornamento sia avvenuto
                        const newSettings = sceneEditor.getTryOnSettings();
                        console.log('🔍 TryOnSettings after update:', newSettings);
                    }
                } catch (error) {
                    console.error('❌ Error updating styleButton:', error);
                }
            }
        },
        [sceneEditor]
    );

    // Effetto per controllare se il VTO è già attivo leggendo dai settings e dal config scena
    React.useEffect(() => {
        const productTryOn = productTryOnQuery.productTryOn;
        let sceneConfig = null;

        // 🔧 NUOVO: Variabili per tenere traccia di cosa abbiamo trovato nel sceneEditor
        let hasSceneEditorData = false;
        let sceneEditorValues = {
            isVTOEnabled: false,
            selectedType: null as TryOnType | null,
            virtualTryOnEnabled: false,
            pupillaryDistanceEnabled: false,
            mandatoryPDEnabled: false
        };

        // Ottieni il config della scena se disponibile
        if (sceneEditor) {
            try {
                sceneConfig = sceneEditor.getSceneConfig();
                console.log('🔍 Scene config:', sceneConfig);

                // Leggi lo stato VTO dal sceneEditor
                const currentTryOnSettings = sceneEditor.getTryOnSettings?.();
                const isVTOEnabledInScene = sceneEditor.getTryOnEnabled?.() ?? false;
                const selectedTypeFromScene = sceneEditor.getTryOnSelectedType?.();

                console.log('🔍 VTO state from sceneEditor:', {
                    isEnabled: isVTOEnabledInScene,
                    selectedType: selectedTypeFromScene,
                    settings: currentTryOnSettings
                });

                // Se ci sono impostazioni TryOn nel sceneEditor, usale come priorità ASSOLUTA
                if (currentTryOnSettings || isVTOEnabledInScene) {
                    console.log('✅ PRIORITÀ ASSOLUTA: Using sceneEditor data for VTO initialization');
                    hasSceneEditorData = true;

                    // Salva i valori dal sceneEditor
                    sceneEditorValues.isVTOEnabled = isVTOEnabledInScene;
                    sceneEditorValues.selectedType = selectedTypeFromScene;

                    // Leggi le impostazioni specifiche del tipo dalle impostazioni TryOn
                    if (currentTryOnSettings && currentTryOnSettings.typeSettings) {
                        const virtualTryOnValue = currentTryOnSettings.typeSettings.try_on;
                        const pupillaryDistanceValue = currentTryOnSettings.typeSettings.pd ?? false;
                        const typeSettingsAny = currentTryOnSettings.typeSettings as any;
                        console.log('typeSettingsAny:', typeSettingsAny);
                        const mandatoryPDValue = typeSettingsAny.mandatoryPD !== undefined ? typeSettingsAny.mandatoryPD : pupillaryDistanceValue; // Fallback per retrocompatibilità

                        sceneEditorValues.virtualTryOnEnabled = virtualTryOnValue;
                        sceneEditorValues.pupillaryDistanceEnabled = pupillaryDistanceValue;
                        sceneEditorValues.mandatoryPDEnabled = mandatoryPDValue;

                        console.log('🔍 Eyewear settings from sceneEditor (ASSOLUTI):', {
                            try_on: virtualTryOnValue,
                            pd: pupillaryDistanceValue,
                            mandatoryPD: mandatoryPDValue,
                            hasMandatoryPDField: typeSettingsAny.mandatoryPD !== undefined,
                            typeSettingsRaw: currentTryOnSettings.typeSettings
                        });
                    }

                    // 🔧 SEMPRE carica le customizzazioni dei bottoni se esistono (indipendentemente da abilitazione)
                    if (currentTryOnSettings && currentTryOnSettings.buttonCustomizations && currentTryOnSettings.buttonCustomizations.length > 0) {
                        console.log('🎨 Loading button customizations from sceneEditor (ALWAYS):', currentTryOnSettings.buttonCustomizations);

                        // Primo bottone (Virtual Try-On) - indice 0 - SEMPRE caricato
                        const firstButton = currentTryOnSettings.buttonCustomizations[0];
                        if (firstButton) {
                            const imageField = firstButton.image || '';
                            console.log('🖼️ Loading first button (Virtual Try-On) image - ALWAYS:', {
                                imageFromAPI: firstButton.image,
                                imageLength: imageField ? imageField.length : 0,
                                selected: imageField ? 'Found' : 'Empty'
                            });

                            setButtonCustomizations((prev) => ({
                                ...prev,
                                iconImage: imageField,
                                textLabel: firstButton.textLabel || prev.textLabel || 'Virtual Try-on',
                                textColor: firstButton.textColor || prev.textColor || '#000000',
                                backgroundColor: firstButton.backgroundColor || prev.backgroundColor || '#F4F4F4',
                                borderColor: firstButton.borderColor || prev.borderColor || '#F4F4F4',
                                borderSize: firstButton.borderWidth ? `${firstButton.borderWidth}px` : prev.borderSize || '1px',
                                borderRadius: firstButton.borderRadius ? `${firstButton.borderRadius}px` : prev.borderRadius || '0px'
                            }));
                        }

                        // Secondo bottone (Pupillary Distance) - indice 1 - SEMPRE caricato se esiste
                        if (currentTryOnSettings.buttonCustomizations.length > 1) {
                            const secondButton = currentTryOnSettings.buttonCustomizations[1];
                            if (secondButton) {
                                const imageField = secondButton.image || '';
                                console.log('🖼️ Loading second button (Pupillary Distance) image - ALWAYS:', {
                                    imageFromAPI: secondButton.image,
                                    imageLength: imageField ? imageField.length : 0,
                                    selected: imageField ? 'Found' : 'Empty'
                                });

                                setPupillaryDistanceCustomizations((prev) => ({
                                    ...prev,
                                    iconImage: imageField,
                                    textLabel: secondButton.textLabel || prev.textLabel || 'Pupillary Distance',
                                    textColor: secondButton.textColor || prev.textColor || '#000000',
                                    backgroundColor: secondButton.backgroundColor || prev.backgroundColor || '#F4F4F4',
                                    borderColor: secondButton.borderColor || prev.borderColor || '#F4F4F4',
                                    borderSize: secondButton.borderWidth ? `${secondButton.borderWidth}px` : prev.borderSize || '1px',
                                    borderRadius: secondButton.borderRadius ? `${secondButton.borderRadius}px` : prev.borderRadius || '0px'
                                }));
                            }
                        }

                        console.log('✅ All button customizations loaded from sceneEditor - ALWAYS APPLIED');
                    }
                }
            } catch (error) {
                console.error('Error getting scene config or TryOn settings:', error);
            }
        }

        // 🔧 IMPORTANTE: Solo se NON abbiamo dati dal sceneEditor, usa i dati del database
        if (!hasSceneEditorData && productTryOn && productTryOn.settings) {
            try {
                const parsedSettings: VTOSettings = JSON.parse(productTryOn.settings);
                setVtoSettings(parsedSettings);

                // 🔧 SEMPRE carica le customizzazioni dei bottoni se disponibili nel database (indipendentemente da VTO attivo)
                if (parsedSettings.buttonCustomizations) {
                    console.log('🎨 Loading button customizations from DATABASE - ALWAYS:', parsedSettings.buttonCustomizations);

                    // 🔧 NUOVO: Gestione separata per primo e secondo bottone
                    if (Array.isArray(parsedSettings.buttonCustomizations)) {
                        // Carica come array (nuovo formato)
                        if (parsedSettings.buttonCustomizations.length > 0) {
                            const firstButton = parsedSettings.buttonCustomizations[0];
                            setButtonCustomizations((prev) => ({
                                ...prev,
                                iconImage: firstButton.image || firstButton.iconImage || prev.iconImage,
                                textLabel: firstButton.textLabel || prev.textLabel,
                                textColor: firstButton.textColor || prev.textColor,
                                backgroundColor: firstButton.backgroundColor || prev.backgroundColor,
                                borderColor: firstButton.borderColor || prev.borderColor,
                                borderSize: firstButton.borderWidth ? `${firstButton.borderWidth}px` : firstButton.borderSize || prev.borderSize,
                                borderRadius: firstButton.borderRadius ? `${firstButton.borderRadius}px` : firstButton.borderRadius || prev.borderRadius
                            }));
                        }

                        // Secondo bottone (Pupillary Distance)
                        if (parsedSettings.buttonCustomizations.length > 1) {
                            const secondButton = parsedSettings.buttonCustomizations[1];
                            setPupillaryDistanceCustomizations((prev) => ({
                                ...prev,
                                iconImage: secondButton.image || secondButton.iconImage || prev.iconImage,
                                textLabel: secondButton.textLabel || prev.textLabel,
                                textColor: secondButton.textColor || prev.textColor,
                                backgroundColor: secondButton.backgroundColor || prev.backgroundColor,
                                borderColor: secondButton.borderColor || prev.borderColor,
                                borderSize: secondButton.borderWidth ? `${secondButton.borderWidth}px` : secondButton.borderSize || prev.borderSize,
                                borderRadius: secondButton.borderRadius ? `${secondButton.borderRadius}px` : secondButton.borderRadius || prev.borderRadius
                            }));
                        }
                    } else {
                        // Formato legacy (oggetto singolo) - solo primo bottone
                        setButtonCustomizations((prev) => ({
                            ...prev,
                            ...parsedSettings.buttonCustomizations
                        }));
                    }
                }

                // Imposta lo stato VTO basato sui database settings (SOLO se non abbiamo dati dal sceneEditor)
                if (parsedSettings.active === true) {
                    console.log('📊 Using DATABASE settings (no sceneEditor data found)');

                    sceneEditorValues.isVTOEnabled = true;
                    sceneEditorValues.virtualTryOnEnabled = parsedSettings.try_on === true;
                    sceneEditorValues.pupillaryDistanceEnabled = parsedSettings.pd === true;
                    // 🔧 CORRETTO: Leggi il Mandatory PD dal campo separato se disponibile, altrimenti usa il valore del PD (retrocompatibilità)
                    const mandatoryPDValue = parsedSettings.mandatoryPD !== undefined ? parsedSettings.mandatoryPD === true : parsedSettings.pd === true;
                    sceneEditorValues.mandatoryPDEnabled = mandatoryPDValue;

                    console.log('📊 Database settings loaded:', {
                        active: parsedSettings.active,
                        try_on: parsedSettings.try_on,
                        pd: parsedSettings.pd,
                        mandatoryPD: parsedSettings.mandatoryPD,
                        mandatoryPD_type: typeof parsedSettings.mandatoryPD,
                        mandatoryPD_undefined: parsedSettings.mandatoryPD === undefined,
                        calculated_mandatoryPD: mandatoryPDValue,
                        hasManadtoryPDField: parsedSettings.mandatoryPD !== undefined
                    });

                    // Se è attivo, imposta provider e type dai dati del prodotto
                    if (productTryOn.providerID) {
                        setSelectedProvider(productTryOn.providerID);
                    }
                    if (productTryOn.typeID) {
                        // Trova il TryOnType corrispondente al typeID
                        const matchingType = tryOnTypeOptions.find((opt) => opt.value === productTryOn.typeID);
                        if (matchingType) {
                            console.log('🎯 Loading VTO type from database:', matchingType.value);
                            sceneEditorValues.selectedType = matchingType.value;
                        }
                    }

                    // Carica i dati del customer se disponibili
                    if (productTryOn.typeID && productTryOn.providerID) {
                        loadCustomerData(productTryOn.typeID, productTryOn.providerID);
                    }
                } else {
                    // 🔧 NUOVO: Anche se VTO non è attivo, carica comunque provider e type per le customizzazioni
                    console.log('📊 VTO not active, but loading provider/type for button customizations');
                    if (productTryOn.providerID) {
                        setSelectedProvider(productTryOn.providerID);
                    }
                    if (productTryOn.typeID) {
                        const matchingType = tryOnTypeOptions.find((opt) => opt.value === productTryOn.typeID);
                        if (matchingType) {
                            console.log('🎯 Loading VTO type from database (for customizations):', matchingType.value);
                            // Non impostiamo sceneEditorValues.selectedType qui perché VTO non è attivo
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing VTO settings:', error);
                setVtoSettings(null);
            }
        } else if (hasSceneEditorData) {
            console.log('🚫 SKIPPING database settings - using sceneEditor data as absolute priority');
            // Comunque carichiamo i dati per provider e type se necessario
            if (productTryOn && productTryOn.providerID) {
                setSelectedProvider(productTryOn.providerID);
            }
            if (productTryOn && productTryOn.typeID && !sceneEditorValues.selectedType) {
                const matchingType = tryOnTypeOptions.find((opt) => opt.value === productTryOn.typeID);
                if (matchingType) {
                    console.log('🎯 Loading VTO type from database (fallback):', matchingType.value);
                    sceneEditorValues.selectedType = matchingType.value;
                }
            }
        }

        // 🔧 IMPORTANTE: Applica i valori finali al state
        setIsVTOEnabled(sceneEditorValues.isVTOEnabled);
        setSelectedType(sceneEditorValues.selectedType);
        setVirtualTryOnEnabled(sceneEditorValues.virtualTryOnEnabled);
        setPupillaryDistanceEnabled(sceneEditorValues.pupillaryDistanceEnabled);
        setMandatoryPDEnabled(sceneEditorValues.mandatoryPDEnabled);

        console.log('🎯 FINAL VALUES APPLIED:', sceneEditorValues);

        // 🔧 IMPORTANTE: Segna che l'inizializzazione è completata
        setIsInitializing(false);
    }, [productTryOnQuery, sceneEditor]);

    // 🔧 NUOVO: Effect per sincronizzare i valori letti dal database nei typeSettings del sceneEditor
    React.useEffect(() => {
        // 🔧 IMPORTANTE: Solo se VTO è abilitato, tipo è Eyewear E l'inizializzazione è completata
        if (!isInitializing && sceneEditor && isVTOEnabled && selectedType === TryOnType.Eyewear) {
            try {
                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                if (currentTryOnSettings) {
                    const updatedTypeSettings = {
                        ...currentTryOnSettings.typeSettings,
                        try_on: virtualTryOnEnabled,
                        pd: pupillaryDistanceEnabled,
                        mandatoryPD: mandatoryPDEnabled // Aggiorna esplicitamente il campo mandatoryPD
                    };

                    const updatedSettings = {
                        ...currentTryOnSettings,
                        typeSettings: updatedTypeSettings
                    };

                    sceneEditor.setTryOnTypeSettings(updatedSettings);
                    console.log('🔄 Synced values to sceneEditor typeSettings (post-init):', {
                        try_on: virtualTryOnEnabled,
                        pd: pupillaryDistanceEnabled,
                        mandatoryPD: mandatoryPDEnabled
                    });
                }
            } catch (error) {
                console.error('❌ Error syncing values to sceneEditor:', error);
            }
        } else if (!isInitializing && sceneEditor && !isVTOEnabled) {
            // 🔧 NUOVO: Se VTO è disabilitato, assicurati che i typeSettings siano puliti
            console.log('🧹 VTO is disabled, ensuring typeSettings are cleared (post-init)');
        }
    }, [isInitializing, sceneEditor, isVTOEnabled, selectedType, virtualTryOnEnabled, pupillaryDistanceEnabled, mandatoryPDEnabled]);

    // E il motore deve saperlo mentre lo si sceglie: qui il tipo cambia sotto le dita, e il provider
    // deve seguirlo senza aspettare un salvataggio.
    React.useEffect(() => {
        if (!sceneEditor?.setProvider) return;

        sceneEditor.setProvider(resolveTryOnProvider(providersQuery, productTryOnQuery, selectedType, isVTOEnabled));
    }, [sceneEditor, isVTOEnabled, selectedType, providersQuery, productTryOnQuery]);

    // 🔧 NUOVO: Funzione di debug per controllare lo stato VTO prima del salvataggio
    const debugVTOState = React.useCallback(() => {
        console.log('🔍 =====  DEBUG VTO STATE =====');
        console.log('Local State:', {
            isVTOEnabled,
            selectedType,
            selectedProvider,
            virtualTryOnEnabled,
            pupillaryDistanceEnabled,
            mandatoryPDEnabled,
            isInitializing
        });

        if (sceneEditor) {
            try {
                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                const vtoEnabled = sceneEditor.getTryOnEnabled?.();
                const selectedTypeFromEditor = sceneEditor.getTryOnSelectedType?.();

                console.log('SceneEditor State:', {
                    tryOnEnabled: vtoEnabled,
                    selectedType: selectedTypeFromEditor,
                    currentTryOnSettings: currentTryOnSettings
                });

                if (currentTryOnSettings && currentTryOnSettings.typeSettings) {
                    console.log('TypeSettings Detail:', currentTryOnSettings.typeSettings);
                }
            } catch (error) {
                console.error('❌ Error reading sceneEditor state:', error);
            }
        }
        console.log('🔍 ===== END DEBUG VTO STATE =====');
    }, [sceneEditor, isVTOEnabled, selectedType, selectedProvider, virtualTryOnEnabled, pupillaryDistanceEnabled, mandatoryPDEnabled, isInitializing]);

    // Esponi la funzione di debug globalmente per debugging
    React.useEffect(() => {
        (window as any).debugVTOState = debugVTOState;
        return () => {
            delete (window as any).debugVTOState;
        };
    }, [debugVTOState]);

    // Effect per controllare i VTO reference files quando cambia lo stato VTO
    React.useEffect(() => {
        if (sceneEditor && isVTOEnabled) {
            checkForVTOReferenceFiles();
        } else {
            setHasVTOReferenceModels(false);
        }
    }, [sceneEditor, isVTOEnabled, selectedType]);

    // Effect per aggiornare il styleButton quando cambiano gli stati eyewear
    React.useEffect(() => {
        if (sceneEditor && selectedType) {
            console.log('🔧 StyleButton useEffect triggered:', {
                selectedType,
                virtualTryOnEnabled,
                pupillaryDistanceEnabled
            });
            updateStyleButton(selectedType, virtualTryOnEnabled, pupillaryDistanceEnabled);
        }
    }, [selectedType, virtualTryOnEnabled, pupillaryDistanceEnabled, updateStyleButton]);

    const loadCustomerData = async (typeId: number, providerId: number) => {
        try {
            // Questa query deve essere eseguita condizionalmente
            console.log('Loading customer data for typeId:', typeId, 'providerId:', providerId);
            // TODO: Implementare la query condizionale per tryOnCustomer
        } catch (error) {
            console.error('Error loading customer data:', error);
        }
    };

    const handleVTOToggle = (enabled: boolean) => {
        setIsVTOEnabled(enabled);

        // 🔧 SINCRONIZZAZIONE: Usa gli stessi metodi del vecchio VTO Tree
        if (sceneEditor) {
            try {
                // Controlla se il sceneEditor ha il metodo del vecchio editor
                if (typeof sceneEditor.setTryOnEnabled === 'function') {
                    sceneEditor.setTryOnEnabled(enabled);
                } else {
                    // Fallback ai metodi diretti
                    sceneEditor.setTryOnEnabled?.(enabled);
                }

                if (enabled) {
                    // Se viene abilitato, imposta il tipo selezionato se disponibile
                    const currentType = sceneEditor.getTryOnSelectedType?.();
                    if (selectedType && currentType !== selectedType) {
                        if (typeof sceneEditor.setTryOnSelectedType === 'function') {
                            sceneEditor.setTryOnSelectedType(selectedType);
                        }
                    }
                } else {
                    // Se viene disabilitato, rimuovi i riferimenti VTO e resetta il tipo
                    const meshes = sceneEditor.removeAllVTOReferenceMeshes?.();
                    if (typeof sceneEditor.setTryOnSelectedType === 'function') {
                        sceneEditor.setTryOnSelectedType(0); // TryOnType.NotSelected
                    }

                    // 🔧 IMPORTANTE: Reset anche dei typeSettings quando si disabilita VTO
                    try {
                        const currentTryOnSettings = sceneEditor.getTryOnSettings();
                        if (currentTryOnSettings && currentTryOnSettings.typeSettings) {
                            const clearedTypeSettings = {
                                ...currentTryOnSettings.typeSettings,
                                try_on: false,
                                pd: false,
                                mandatoryPD: false
                            };

                            const updatedSettings = {
                                ...currentTryOnSettings,
                                typeSettings: clearedTypeSettings
                            };

                            sceneEditor.setTryOnTypeSettings(updatedSettings);
                            console.log('✅ VTO typeSettings cleared when disabling VTO');
                        }
                    } catch (error) {
                        console.error('❌ Error clearing typeSettings:', error);
                    }

                    sceneEditor.setRenderingNeeded?.();
                    console.log('✅ VTO disabled using OLD methods');
                }
            } catch (error) {
                console.error('❌ Error updating VTO state:', error);
            }
        }

        if (!enabled) {
            setSelectedProvider(null);
            setSelectedType(null);
            // Resetta anche le opzioni Eyewear quando il VTO è disabilitato
            setVirtualTryOnEnabled(false);
            setPupillaryDistanceEnabled(false);
            setMandatoryPDEnabled(false);
        } else {
            // Se viene attivato, carica sempre le reference files se c'è un tipo selezionato
            if (selectedType && sceneEditor) {
                addVTOReferenceFilesToScene(selectedType);
            } else {
                // Se non c'è un tipo selezionato, seleziona il primo disponibile
                const availableTypes = tryOnTypeOptions.filter((opt) => !opt.disabled);
                if (availableTypes.length > 0) {
                    const firstTypeValue = availableTypes[0].value;
                    setSelectedType(firstTypeValue);

                    // 🔧 FIX: Imposta anche il tipo nel sceneEditor immediatamente
                    if (sceneEditor) {
                        try {
                            const editorWithOldMethods = sceneEditor as any;
                            if (typeof editorWithOldMethods.setTryOnSelectedType === 'function') {
                                editorWithOldMethods.setTryOnSelectedType(firstTypeValue);
                            } else {
                                sceneEditor.setTryOnSelectedType?.(firstTypeValue);
                            }
                        } catch (error) {
                            console.error('❌ Error setting auto-selected VTO type in sceneEditor:', error);
                        }

                        // Auto-carica le reference files quando viene preselezionato il primo tipo
                        addVTOReferenceFilesToScene(firstTypeValue);
                    }
                } else {
                    console.warn('⚠️ No available VTO types found for auto-selection');
                }
            }

            // Se il tipo selezionato è Eyewear, abilita automaticamente la Virtual Try-On
            // 🔧 FIX: Controlla sia il tipo corrente che quello appena selezionato automaticamente
            const availableTypesForCheck = tryOnTypeOptions.filter((opt) => !opt.disabled);
            const effectiveSelectedType = selectedType || (availableTypesForCheck.length > 0 ? availableTypesForCheck[0].value : null);
            if (effectiveSelectedType === TryOnType.Eyewear) {
                setVirtualTryOnEnabled(true);
                // 🔧 FIX: Imposta anche nel sceneEditor
                if (sceneEditor) {
                    try {
                        const editorWithOldMethods = sceneEditor as any;
                        if (typeof editorWithOldMethods.setTryOnVirtualTryOnEnabled === 'function') {
                            editorWithOldMethods.setTryOnVirtualTryOnEnabled(true);
                        } else {
                            sceneEditor.setTryOnSetting?.(TryOnSettingsType.VirtualTryOn, true);
                        }
                    } catch (error) {
                        console.error('❌ Error auto-enabling Virtual Try-On:', error);
                    }
                }
            }
        }
    };

    // Funzione per gestire i cambiamenti delle opzioni Eyewear
    const handleEyewearOptionChange = (optionType: 'virtual_tryon' | 'pupillary_distance', enabled: boolean) => {
        if (optionType === 'virtual_tryon') {
            setVirtualTryOnEnabled(enabled);
        } else {
            setPupillaryDistanceEnabled(enabled);
            // Quando il Pupillary Distance viene disabilitato, disabilita anche il Mandatory PD
            if (!enabled) {
                setMandatoryPDEnabled(false);
                // Aggiorna anche il sceneEditor per il Mandatory PD
                if (sceneEditor) {
                    sceneEditor.setTryOnSetting?.(TryOnSettingsType.MandatoryPupillaryDistance, false);

                    // 🔧 IMPORTANTE: Aggiorna anche i typeSettings per assicurare la persistenza
                    try {
                        const currentTryOnSettings = sceneEditor.getTryOnSettings();
                        if (currentTryOnSettings && currentTryOnSettings.typeSettings) {
                            const updatedTypeSettings = {
                                ...currentTryOnSettings.typeSettings,
                                mandatoryPD: false // Disabilita esplicitamente il mandatory quando si disabilita PD
                            };

                            const updatedSettings = {
                                ...currentTryOnSettings,
                                typeSettings: updatedTypeSettings
                            };

                            sceneEditor.setTryOnTypeSettings(updatedSettings);
                        }
                    } catch (error) {
                        console.error('❌ Error updating typeSettings when disabling PD:', error);
                    }
                }
            }
        }

        // Determina i nuovi valori dopo il cambiamento
        const newVirtualTryOnEnabled = optionType === 'virtual_tryon' ? enabled : virtualTryOnEnabled;
        const newPupillaryDistanceEnabled = optionType === 'pupillary_distance' ? enabled : pupillaryDistanceEnabled;

        // Il styleButton verrà aggiornato automaticamente dal useEffect quando gli stati cambiano

        // 🔧 SINCRONIZZAZIONE: Usa gli stessi metodi del vecchio VTO Tree
        if (sceneEditor) {
            try {
                if (optionType === 'virtual_tryon') {
                    // Usa il metodo del vecchio VTO Tree se disponibile tramite cast
                    const editorWithOldMethods = sceneEditor as any;
                    if (typeof editorWithOldMethods.setTryOnVirtualTryOnEnabled === 'function') {
                        editorWithOldMethods.setTryOnVirtualTryOnEnabled(enabled);
                    } else {
                        // Fallback al metodo nuovo
                        sceneEditor.setTryOnSetting?.(TryOnSettingsType.VirtualTryOn, enabled);
                    }
                } else {
                    // Pupillary Distance
                    const editorWithOldMethods = sceneEditor as any;
                    if (typeof editorWithOldMethods.setTryOnGlassPDEnabled === 'function') {
                        editorWithOldMethods.setTryOnGlassPDEnabled(enabled);
                    } else {
                        // Fallback al metodo nuovo
                        sceneEditor.setTryOnSetting?.(TryOnSettingsType.GlassPupillaryDistanceTool, enabled);
                    }
                }
            } catch (error) {
                console.error('❌ Error updating eyewear option:', error);
            }
        }

        // Controlla se entrambe le opzioni saranno disattivate dopo questo cambiamento
        const willVirtualTryOnBeDisabled = optionType === 'virtual_tryon' ? !enabled : !virtualTryOnEnabled;
        const willPupillaryDistanceBeDisabled = optionType === 'pupillary_distance' ? !enabled : !pupillaryDistanceEnabled;

        // Se entrambe le opzioni Eyewear sono disattivate, disattiva anche il VTO padre
        if (willVirtualTryOnBeDisabled && willPupillaryDistanceBeDisabled) {
            setIsVTOEnabled(false);
            setSelectedProvider(null);
            setSelectedType(null);

            // 🔧 SINCRONIZZAZIONE: Disabilita il VTO usando gli stessi metodi del vecchio VTO Tree
            if (sceneEditor) {
                try {
                    if (typeof sceneEditor.setTryOnEnabled === 'function') {
                        sceneEditor.setTryOnEnabled(false);
                        sceneEditor.setTryOnSelectedType?.(TryOnType.NotSelected);
                    } else {
                        // Fallback ai metodi nuovi
                        sceneEditor.setTryOnEnabled?.(false);
                        sceneEditor.setTryOnSelectedType?.(TryOnType.NotSelected);
                    }
                    sceneEditor.setRenderingNeeded?.();
                } catch (error) {
                    console.error('❌ Error disabling VTO:', error);
                }
            }
        }
    };

    // 🔧 NUOVO: Funzione per gestire il Mandatory PD separatamente
    const handleMandatoryPDChange = (enabled: boolean) => {
        setMandatoryPDEnabled(enabled);

        // Aggiorna anche il sceneEditor per il Mandatory PD usando entrambi i metodi per sicurezza
        if (sceneEditor) {
            try {
                // Metodo specifico per Mandatory PD
                sceneEditor.setTryOnSetting?.(TryOnSettingsType.MandatoryPupillaryDistance, enabled);

                // 🔧 IMPORTANTE: Aggiorna anche i typeSettings completi per assicurare la persistenza
                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                if (currentTryOnSettings && currentTryOnSettings.typeSettings) {
                    const updatedTypeSettings = {
                        ...currentTryOnSettings.typeSettings,
                        mandatoryPD: enabled // Aggiungi esplicitamente il campo mandatoryPD
                    };

                    const updatedSettings = {
                        ...currentTryOnSettings,
                        typeSettings: updatedTypeSettings
                    };

                    sceneEditor.setTryOnTypeSettings(updatedSettings);
                }
            } catch (error) {
                console.error('❌ Error updating Mandatory PD:', error);
            }
        }
    };

    // Funzione per resettare il text label
    const handleResetTextLabel = () => {
        setButtonCustomizations((prev) => ({ ...prev, textLabel: 'Virtual Try-on' }));
        updateTryOnSettings({ textLabel: 'Virtual Try-on' });
    };

    // 🔧 AGGIORNATO: Funzione per aggiornare il primo bottone (Virtual Try-On)
    const updateVirtualTryOnSettings = (buttonChanges: Partial<typeof buttonCustomizations>) => {
        if (sceneEditor) {
            try {
                const currentTryOnSettings = sceneEditor.getTryOnSettings();

                if (currentTryOnSettings) {
                    const mappedChanges: any = { ...buttonChanges };

                    // Mappa iconImage -> image per l'API
                    if ('iconImage' in mappedChanges) {
                        mappedChanges.image = mappedChanges.iconImage;
                        delete mappedChanges.iconImage;
                    }

                    // Converti borderSize e borderRadius da stringhe con "px" a numeri
                    if ('borderSize' in mappedChanges) {
                        mappedChanges.borderWidth = parseInt(mappedChanges.borderSize) || 1;
                        delete mappedChanges.borderSize;
                    }
                    if ('borderRadius' in mappedChanges) {
                        mappedChanges.borderRadius = parseInt(mappedChanges.borderRadius) || 0;
                    }

                    // Aggiorna solo il primo bottone (indice 0)
                    const updatedButtonCustomizations = [...(currentTryOnSettings.buttonCustomizations || [])];

                    if (updatedButtonCustomizations.length === 0) {
                        // Se non esistono bottoni, crea il primo con fallback ai valori di default
                        updatedButtonCustomizations.push({
                            image: buttonCustomizations.iconImage || 'base',
                            textLabel: buttonCustomizations.textLabel || 'Virtual Try-on',
                            textColor: buttonCustomizations.textColor || '#000000',
                            backgroundColor: buttonCustomizations.backgroundColor || '#F4F4F4',
                            borderColor: buttonCustomizations.borderColor || '#F4F4F4',
                            borderWidth: parseInt(buttonCustomizations.borderSize) || 1,
                            borderRadius: parseInt(buttonCustomizations.borderRadius) || 0,
                            ...mappedChanges
                        });
                    } else {
                        // Aggiorna il primo bottone esistente
                        updatedButtonCustomizations[0] = {
                            ...updatedButtonCustomizations[0],
                            ...mappedChanges
                        };
                    }
                    const updatedSettings = {
                        ...currentTryOnSettings,
                        buttonCustomizations: updatedButtonCustomizations
                    };

                    sceneEditor.setTryOnTypeSettings(updatedSettings);
                    onSceneChanged?.();
                    console.log('✅ Virtual Try-On button updated successfully');
                }
            } catch (error) {
                console.error('❌ Error updating Virtual Try-On button:', error);
            }
        }
    };

    // 🔧 NUOVO: Funzione per aggiornare il secondo bottone (Pupillary Distance)
    const updatePupillaryDistanceSettings = (buttonChanges: Partial<typeof pupillaryDistanceCustomizations>) => {
        if (sceneEditor) {
            try {
                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                if (currentTryOnSettings) {
                    const mappedChanges: any = { ...buttonChanges };

                    // Mappa iconImage -> image per l'API
                    if ('iconImage' in mappedChanges) {
                        mappedChanges.image = mappedChanges.iconImage || 'base';
                        delete mappedChanges.iconImage;
                    }

                    // Converti borderSize e borderRadius da stringhe con "px" a numeri
                    if ('borderSize' in mappedChanges) {
                        mappedChanges.borderWidth = parseInt(mappedChanges.borderSize) || 1;
                        delete mappedChanges.borderSize;
                    }
                    if ('borderRadius' in mappedChanges) {
                        mappedChanges.borderRadius = parseInt(mappedChanges.borderRadius) || 0;
                    }

                    // Aggiorna il secondo bottone (indice 1)
                    const updatedButtonCustomizations = [...(currentTryOnSettings.buttonCustomizations || [])];

                    // Assicurati che esista almeno il primo bottone
                    while (updatedButtonCustomizations.length < 2) {
                        if (updatedButtonCustomizations.length === 0) {
                            // Aggiungi il primo bottone con le impostazioni correnti e fallback
                            updatedButtonCustomizations.push({
                                image: buttonCustomizations.iconImage || 'base',
                                textLabel: buttonCustomizations.textLabel || 'Virtual Try-on',
                                textColor: buttonCustomizations.textColor || '#000000',
                                backgroundColor: buttonCustomizations.backgroundColor || '#F4F4F4',
                                borderColor: buttonCustomizations.borderColor || '#F4F4F4',
                                borderWidth: parseInt(buttonCustomizations.borderSize) || 1,
                                borderRadius: parseInt(buttonCustomizations.borderRadius) || 0
                            });
                        } else {
                            // Aggiungi il secondo bottone con fallback
                            updatedButtonCustomizations.push({
                                image: pupillaryDistanceCustomizations.iconImage || 'base',
                                textLabel: pupillaryDistanceCustomizations.textLabel || 'Pupillary Distance',
                                textColor: pupillaryDistanceCustomizations.textColor || '#000000',
                                backgroundColor: pupillaryDistanceCustomizations.backgroundColor || '#F4F4F4',
                                borderColor: pupillaryDistanceCustomizations.borderColor || '#F4F4F4',
                                borderWidth: parseInt(pupillaryDistanceCustomizations.borderSize) || 1,
                                borderRadius: parseInt(pupillaryDistanceCustomizations.borderRadius) || 0,
                                ...mappedChanges
                            });
                        }
                    }

                    // Aggiorna il secondo bottone esistente
                    updatedButtonCustomizations[1] = {
                        ...updatedButtonCustomizations[1],
                        ...mappedChanges
                    };

                    const updatedSettings = {
                        ...currentTryOnSettings,
                        buttonCustomizations: updatedButtonCustomizations
                    };

                    sceneEditor.setTryOnTypeSettings(updatedSettings);
                    onSceneChanged?.();
                    console.log('✅ Pupillary Distance button updated successfully');
                }
            } catch (error) {
                console.error('❌ Error updating Pupillary Distance button:', error);
            }
        }
    };

    // 🔧 DEPRECATED: Funzione di retrocompatibilità (aggiorna il primo bottone)
    const updateTryOnSettings = (buttonChanges: Partial<typeof buttonCustomizations>) => {
        updateVirtualTryOnSettings(buttonChanges);
    };

    // 🔧 AGGIORNATO: Funzione per aggiornare tutte le impostazioni al salvataggio
    const updateAllSettings = () => {
        if (sceneEditor) {
            try {
                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                console.log('💾 Saving all TryOn settings:', currentTryOnSettings);

                if (currentTryOnSettings) {
                    // 🔧 NUOVO: Gestisce entrambi i bottoni nell'array
                    const updatedButtonCustomizations = [];

                    // Primo bottone (Virtual Try-On) - sempre presente
                    updatedButtonCustomizations.push({
                        image: buttonCustomizations.iconImage || 'base',
                        textLabel: buttonCustomizations.textLabel || 'Virtual Try-on',
                        textColor: buttonCustomizations.textColor || '#000000',
                        backgroundColor: buttonCustomizations.backgroundColor || '#F4F4F4',
                        borderColor: buttonCustomizations.borderColor || '#F4F4F4',
                        borderWidth: parseInt(buttonCustomizations.borderSize) || 1,
                        borderRadius: parseInt(buttonCustomizations.borderRadius) || 0
                    });

                    // Secondo bottone (Pupillary Distance) - solo se abilitato per Eyewear
                    if (selectedType === TryOnType.Eyewear && pupillaryDistanceEnabled) {
                        updatedButtonCustomizations.push({
                            image: pupillaryDistanceCustomizations.iconImage || 'base',
                            textLabel: pupillaryDistanceCustomizations.textLabel || 'Pupillary Distance',
                            textColor: pupillaryDistanceCustomizations.textColor || '#000000',
                            backgroundColor: pupillaryDistanceCustomizations.backgroundColor || '#F4F4F4',
                            borderColor: pupillaryDistanceCustomizations.borderColor || '#F4F4F4',
                            borderWidth: parseInt(pupillaryDistanceCustomizations.borderSize) || 1,
                            borderRadius: parseInt(pupillaryDistanceCustomizations.borderRadius) || 0
                        });
                    }

                    const updatedSettings = {
                        ...currentTryOnSettings,
                        buttonCustomizations: updatedButtonCustomizations
                    };

                    sceneEditor.setTryOnTypeSettings(updatedSettings);
                }
            } catch (error) {
                console.error('❌ Error saving all settings:', error);
            }
        }
    };

    // 🔧 AGGIORNATO: Gestori per i pannelli dei bottoni separati
    const handleVirtualTryOnPanelToggle = () => {
        const newOpenState = !isVirtualTryOnPanelOpen;

        if (!newOpenState) {
            updateAllSettings();
        }

        setIsVirtualTryOnPanelOpen(newOpenState);
    };

    const handlePupillaryDistancePanelToggle = () => {
        const newOpenState = !isPupillaryDistancePanelOpen;

        if (!newOpenState) {
            updateAllSettings();
        }

        setIsPupillaryDistancePanelOpen(newOpenState);
    };

    // 🔧 DEPRECATED: Funzione di retrocompatibilità
    const handleActionButtonPanelToggle = () => {
        handleVirtualTryOnPanelToggle();
    };

    // Funzione per gestire l'upload dell'immagine con validazione migliorata
    const handleImageUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (event: any) => {
            const file = event.target.files[0];
            if (file) {
                // 🔧 FIX: Validazione del file
                const maxSize = 5 * 1024 * 1024; // 5MB
                if (file.size > maxSize) {
                    showErrorNotification({ message: T._('The file is too large: the maximum size is 5 MB.', 'Admin') });
                    return;
                }
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const imageDataUrl = e.target.result as string;

                    // 🔧 FIX: Validazione del base64
                    if (imageDataUrl && imageDataUrl.startsWith('data:image/')) {
                        setButtonCustomizations((prev) => ({ ...prev, iconImage: imageDataUrl }));
                        updateTryOnSettings({ iconImage: imageDataUrl });
                    } else {
                        console.error('❌ Invalid image data format');
                        showErrorNotification({ message: T._('The image could not be loaded: the format is not valid.', 'Admin') });
                    }
                };
                reader.onerror = (error) => {
                    console.error('❌ Error reading file:', error);
                    showErrorNotification({ message: T._('The file could not be loaded.', 'Admin') });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    // Funzione per resettare l'icona
    const handleResetIcon = () => {
        setButtonCustomizations((prev) => ({ ...prev, iconImage: '' }));
        updateTryOnSettings({ iconImage: '' });
    };

    // Funzione per eliminare l'icona
    const handleDeleteIcon = () => {
        setButtonCustomizations((prev) => ({ ...prev, iconImage: '' }));
        updateTryOnSettings({ iconImage: '' });
    };

    // 🔧 NUOVO: Funzioni per gestire il secondo bottone (Pupillary Distance)
    const handlePupillaryDistanceImageUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (event: any) => {
            const file = event.target.files[0];
            if (file) {
                const maxSize = 5 * 1024 * 1024; // 5MB
                if (file.size > maxSize) {
                    showErrorNotification({ message: T._('The file is too large: the maximum size is 5 MB.', 'Admin') });
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const imageDataUrl = e.target.result as string;

                    if (imageDataUrl && imageDataUrl.startsWith('data:image/')) {
                        setPupillaryDistanceCustomizations((prev) => ({ ...prev, iconImage: imageDataUrl }));
                        updatePupillaryDistanceSettings({ iconImage: imageDataUrl });
                    } else {
                        console.error('❌ Invalid image data format');
                        showErrorNotification({ message: T._('The image could not be loaded: the format is not valid.', 'Admin') });
                    }
                };
                reader.onerror = (error) => {
                    console.error('❌ Error reading file:', error);
                    showErrorNotification({ message: T._('The file could not be loaded.', 'Admin') });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const handlePupillaryDistanceResetIcon = () => {
        setPupillaryDistanceCustomizations((prev) => ({ ...prev, iconImage: 'base' }));
        updatePupillaryDistanceSettings({ iconImage: 'base' });
    };

    const handlePupillaryDistanceDeleteIcon = () => {
        setPupillaryDistanceCustomizations((prev) => ({ ...prev, iconImage: 'base' }));
        updatePupillaryDistanceSettings({ iconImage: 'base' });
    };

    // Funzione per resettare il text label del secondo bottone
    const handleResetPupillaryDistanceTextLabel = () => {
        setPupillaryDistanceCustomizations((prev) => ({ ...prev, textLabel: 'Pupillary Distance' }));
        updatePupillaryDistanceSettings({ textLabel: 'Pupillary Distance' });
    };

    // 🔧 NUOVO: Metodi VTO Reference Files
    const getVTOReferencesUrlFromType = async (type: TryOnType, downloadable: boolean = false) => {
        const { DefaultAssetUrls } = await import('@zakeke/zakeke-3d-core');

        switch (type) {
            case TryOnType.Eyewear:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.EyewearDownloadableURL : DefaultAssetUrls.TryonReferences.EyewearURL,
                    name: DefaultAssetUrls.TryonReferences.EyewearName
                };
            case TryOnType.Scarves:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.ScarvesDownloadableURL : DefaultAssetUrls.TryonReferences.ScarvesURL,
                    name: DefaultAssetUrls.TryonReferences.ScarvesName
                };
            case TryOnType.Earrings:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.EarringsDownloadableURL : DefaultAssetUrls.TryonReferences.EarringsURL,
                    name: DefaultAssetUrls.TryonReferences.EarringsName
                };
            case TryOnType.Necklace:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.NecklaceDownloadableURL : DefaultAssetUrls.TryonReferences.NecklaceURL,
                    name: DefaultAssetUrls.TryonReferences.NecklaceName
                };
            case TryOnType.Headwear:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.HeadwearDownloadableURL : DefaultAssetUrls.TryonReferences.HeadwearURL,
                    name: DefaultAssetUrls.TryonReferences.HeadwearName
                };
            case TryOnType.Shoes:
                return {
                    url: downloadable ? DefaultAssetUrls.TryonReferences.ShoeDownloadableURL : DefaultAssetUrls.TryonReferences.ShoeURL,
                    name: DefaultAssetUrls.TryonReferences.ShoeName
                };
            default:
                return { url: '', name: '' };
        }
    };

    const downloadVTOReferenceFiles = async (type: TryOnType) => {
        try {
            const { url, name } = await getVTOReferencesUrlFromType(type, true);
            const { DefaultAssetUrls } = await import('@zakeke/zakeke-3d-core');

            const filename = name + '.glb';
            const link = document.createElement('a');
            link.href = DefaultAssetUrls.TryonReferences.RootUrl + url;
            link.target = '_blank';
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('❌ Error downloading VTO reference files:', error);
        }
    };

    const addVTOReferenceFilesToScene = async (type: TryOnType) => {
        if (!sceneEditor) {
            console.error('❌ SceneEditor not available');
            return;
        }

        try {
            setIsLoadingVTOReferences(true);

            // Rimuovi i file di reference esistenti
            sceneEditor.removeAllVTOReferenceMeshes();

            // Forza un aggiornamento per notificare l'ObjectEditor della rimozione
            sceneEditor.setRenderingNeeded();

            const { url, name } = await getVTOReferencesUrlFromType(type);

            // Aggiungi i nuovi file di reference
            await sceneEditor.createTryOnReferenceModelsAsync(null, name, url);
            sceneEditor.adjustCamera(false);

            // Controlla se ci sono reference files dopo l'aggiunta
            setTimeout(() => {
                const hasRefs = checkForVTOReferenceFiles();

                // Force state update to trigger UI refresh
                if (hasRefs) {
                    // Trigger a state update to ensure icon changes
                    setHasVTOReferenceModels(true);
                }

                setIsLoadingVTOReferences(false);
            }, 1000);

            // 🔧 NUOVO: Controlla le dimensioni del modello dopo il caricamento delle reference
            setTimeout(() => {
                checkVTOReferenceModelsObstruction();
            }, 1500);
        } catch (error) {
            console.error('❌ Error adding VTO reference files to scene:', error);
            setIsLoadingVTOReferences(false);
        }
    };

    const checkForVTOReferenceFiles = () => {
        if (!sceneEditor) return false;

        try {
            if (sceneEditor.getTryOnEnabled()) {
                const meshes = sceneEditor.getSceneMeshes();
                const hasReferenceFiles = meshes.some((mesh: any) => mesh.name && mesh.name.toLowerCase().includes('vto_reference'));

                setHasVTOReferenceModels(hasReferenceFiles);
                return hasReferenceFiles;
            }
        } catch (error) {
            console.error('❌ Error checking for VTO reference files:', error);
        }

        setHasVTOReferenceModels(false);
        return false;
    };

    // 🔧 NUOVO: Controllo delle dimensioni del modello rispetto alla reference
    const checkVTOReferenceModelsObstruction = async () => {
        if (!sceneEditor) {
            return;
        }

        try {
            // Lista di nomi esclusi dal controllo
            const excludedMeshesNames = ['vto_reference', 'ground'];
            const isNameIncluded = (name: string) => {
                return excludedMeshesNames.filter((excludedName) => name.toLowerCase().includes(excludedName)).length > 0;
            };

            // Ottieni tutte le mesh dalla scena
            const allMeshes = sceneEditor.getSceneMeshes();

            // Trova le mesh reference VTO
            const referenceMeshes = allMeshes.filter((mesh: any) => mesh.name && mesh.name.toLowerCase().includes('vto_reference'));

            // Trova le mesh del prodotto (escluse reference e ground, e abilitate per VTO)
            const meshesExcludingReference =
                sceneEditor.scene?.meshes.filter((mesh: any) => !isNameIncluded(mesh.name) && sceneEditor.isTryOnEnabledForMesh(mesh.id)) ?? [];

            // Se non ci sono mesh reference o mesh del prodotto, esci
            if (referenceMeshes.length < 1 || meshesExcludingReference.length < 1) {
                return;
            }

            // Esegui il controllo delle dimensioni
            const valueHidden = sceneEditor.checkMeshHiddenByMeshes(meshesExcludingReference, referenceMeshes, 0.45, 0.55);

            // Crea i dialoghi per le varie situazioni
            const createScalingDialog = (tooLarge: boolean) => {
                const headerText = tooLarge ? T._('Model too large', 'Composer') : T._('Model too small', 'Composer');

                const description = tooLarge
                    ? T._(
                          'For the VTO to work, it is important that your 3D model matches the dimensions of the reference model we provide. However, it seems that your model is much larger.',
                          'Composer'
                      )
                    : T._(
                          'For the VTO to work, it is important that your 3D model matches the dimensions of the reference model we provide. However, it seems that your model is much smaller.',
                          'Composer'
                      );

                return (
                    <EditorDialog
                        size='small'
                        title={headerText}
                        onClose={() => closeDialog('model-size-check')}
                        buttons={[
                            {
                                label: T._('Auto-scale', 'Composer'),
                                onClick: () => {
                                    closeDialog('model-size-check');
                                    // Esegui l'auto-scaling
                                    try {
                                        if (selectedType === TryOnType.Shoes) {
                                            sceneEditor.scaleSceneToTryonReferenceModels('shoes');
                                        } else {
                                            sceneEditor.scaleSceneToTryonReferenceModels();
                                        }
                                        onSceneChanged?.();
                                    } catch (error) {
                                        console.error('❌ Error during auto-scaling:', error);
                                    }
                                }
                            }
                        ]}
                    >
                        <ScalingMessage $gap='12px'>
                            <div>{description}</div>
                            <div>
                                {T._("If you'd like, you can use our automatic scaling system to adjust the model to the correct dimensions.", 'Composer')}
                            </div>
                            <div>
                                {T._(
                                    'If you prefer to scale the model manually, you can download our reference model and use a 3D modeling software to resize your model, then re-upload it here.',
                                    'Composer'
                                )}{' '}
                                <Link
                                    href='https://zakeke.zendesk.com/hc/en-us/articles/11727765160476-Fine-tuning#h_01HHF71NPJG5BM4J0F5R67T3M3'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {T._('Read more here.', 'Composer')}
                                </Link>
                            </div>
                        </ScalingMessage>
                    </EditorDialog>
                );
            };

            // Mostra il dialogo appropriato basato sul risultato
            if (valueHidden === 1) {
                // Modello troppo grande
                showDialog('model-size-check', createScalingDialog(true));
            } else if (valueHidden === -1) {
                // Modello troppo piccolo
                showDialog('model-size-check', createScalingDialog(false));
            } else {
                // Dimensioni OK
            }
        } catch (error) {
            console.error('❌ Error during VTO obstruction check:', error);
        }
    };

    // Prepara le opzioni per la select dei TryOn Types usando le opzioni già definite
    const typeOptions = tryOnTypeOptions
        .map((typeOption) => ({
            value: typeOption.value.toString(),
            label: typeOption.label, // Usa il label già definito (Eyewear, Scarves, etc.)
            disabled: typeOption.disabled
        }))
        .filter((opt) => !opt.disabled); // Filtra solo quelli abilitati

    const referenceTone: ReferenceTone = isLoadingVTOReferences ? 'info' : hasVTOReferenceModels ? 'success' : 'warning';

    return (
        <Container>
            {/* L'INTERRUTTORE DEL TRY-ON STA NELLA TESTATA DEL GRUPPO, come quelli delle altre pagine: spento, il tipo resta
                da leggere ma non si sceglie. I gruppi che seguono dipendono da lui e compaiono solo ad acceso, come prima. */}
            <SettingsGroup
                title={T._('Virtual Try-On Configuration', 'Admin')}
                help={
                    <>
                        {T._(
                            "Enhance your customers' shopping experience by enabling our cutting-edge Virtual Try-On feature. Allow your clients to effortlessly visualize how your products look on them, empowering them to make confident purchase decisions.",
                            'Composer'
                        )}{' '}
                        <TooltipLink
                            href='https://zakeke.zendesk.com/hc/en-us/articles/5137674671132-Virtual-Try-On-for-Configurable-Products'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {T._('Learn more', 'Composer')}
                        </TooltipLink>
                    </>
                }
                off={!isVTOEnabled}
                trailing={
                    <SettingsGroupSwitch
                        id='settings-vto-enabled'
                        label={T._('Enable Virtual Try-On', 'Admin')}
                        checked={isVTOEnabled}
                        onChange={handleVTOToggle}
                    />
                }
            >
                <SettingsFieldRow>
                    <SettingsFieldLabel as='span'>{T._('Select VTO Type', 'Admin')}</SettingsFieldLabel>
                    <SettingControl>
                        <AdvancedSelect
                            aria-label={T._('Select VTO Type', 'Admin')}
                            options={typeOptions}
                            value={typeOptions.find((opt: any) => opt.value === selectedType?.toString()) || null}
                            isDisabled={!isVTOEnabled}
                            onChange={(option: any) => {
                                if (option) {
                                    const typeValue = parseInt(option.value, 10) as TryOnType;
                                    setSelectedType(typeValue);

                                    // Utilizza i metodi corretti del sceneEditor per impostare il tipo
                                    if (sceneEditor) {
                                        try {
                                            const oldType = sceneEditor.getTryOnSelectedType();

                                            // 🔧 SALVA LE CUSTOMIZZAZIONI CORRENTI PRIMA DEL RESET
                                            let savedButtonCustomizations: any[] = [];
                                            if (oldType !== typeValue && oldType !== null && oldType !== undefined) {
                                                const currentTryOnSettings = sceneEditor.getTryOnSettings();
                                                if (currentTryOnSettings?.buttonCustomizations) {
                                                    savedButtonCustomizations = [...currentTryOnSettings.buttonCustomizations];
                                                }
                                            }

                                            // 🔧 SINCRONIZZAZIONE: Usa i metodi compatibili con il vecchio VTO Tree
                                            const editorWithOldMethods = sceneEditor as any;
                                            if (typeof editorWithOldMethods.setTryOnSelectedType === 'function') {
                                                if (oldType !== typeValue) {
                                                    // Usa metodo del vecchio sistema se disponibile
                                                    if (typeof editorWithOldMethods.setTryOnDefaultTypeSettings === 'function') {
                                                        editorWithOldMethods.setTryOnDefaultTypeSettings();
                                                    } else {
                                                        sceneEditor.setTryOnDefaultTypeSettings?.();
                                                    }
                                                }
                                                editorWithOldMethods.setTryOnSelectedType(typeValue);
                                            } else {
                                                // Fallback ai metodi diretti
                                                if (oldType !== typeValue) {
                                                    sceneEditor.setTryOnDefaultTypeSettings?.();
                                                }
                                                sceneEditor.setTryOnSelectedType?.(typeValue);
                                            }

                                            // 🔧 RIPRISTINA LE CUSTOMIZZAZIONI DOPO IL RESET
                                            if (savedButtonCustomizations.length > 0) {
                                                setTimeout(() => {
                                                    const newTryOnSettings = sceneEditor.getTryOnSettings();
                                                    if (newTryOnSettings) {
                                                        const updatedSettings = {
                                                            ...newTryOnSettings,
                                                            buttonCustomizations: savedButtonCustomizations
                                                        };
                                                        sceneEditor.setTryOnTypeSettings(updatedSettings);
                                                    }
                                                }, 100); // Piccolo delay per assicurarsi che il reset sia completato
                                            } else {
                                                // 🔧 APPLICA LE CUSTOMIZZAZIONI LOCALI SE NON ESISTEVANO CUSTOMIZZAZIONI SALVATE
                                                setTimeout(() => {
                                                    updateAllSettings();
                                                }, 150); // Delay leggermente maggiore per assicurarsi che tutto sia pronto
                                            }

                                            // CAMBIARE TIPO BUTTA VIA IL FINETUNING DI PRIMA, come nel vecchio
                                            // backoffice. Il modello esportato e le sue impostazioni parlano del
                                            // tipo precedente — e spessissimo di un altro provider, perché è il
                                            // tipo a sceglierlo — quindi non c'è niente da conservare. E se il
                                            // pannello era rimasto aperto si chiude: quello che mostra non è più
                                            // di questo prodotto.
                                            if (oldType !== typeValue) {
                                                sceneEditor.resetProviderFineTuningData?.();
                                                sceneEditor.showFineTuning = false;
                                                sceneEditor.fineTuningModelLoaded = false;
                                            }

                                            // 🔧 Auto-caricamento reference mesh quando cambia tipo (sempre carica se diverso)
                                            // Carica sempre se oldType è null/undefined o se è diverso dal nuovo tipo
                                            if (oldType !== typeValue || oldType === null || oldType === undefined) {
                                                // Carica automaticamente le reference mesh per il nuovo tipo
                                                addVTOReferenceFilesToScene(typeValue);
                                            }
                                        } catch (error) {
                                            console.error('❌ Error setting VTO type:', error);
                                        }
                                    }

                                    // Se viene selezionato Eyewear, abilita automaticamente la Virtual Try-On
                                    if (typeValue === TryOnType.Eyewear) {
                                        setVirtualTryOnEnabled(true);
                                        // Aggiorna anche nel sceneEditor
                                        if (sceneEditor) {
                                            try {
                                                sceneEditor.setTryOnSetting(TryOnSettingsType.VirtualTryOn, true);
                                            } catch (error) {
                                                console.error('❌ Error enabling Virtual Try-On for Eyewear:', error);
                                            }
                                        }
                                        // Il styleButton verrà aggiornato automaticamente dal useEffect
                                    } else {
                                        // Se non è Eyewear, resetta le opzioni specifiche
                                        setVirtualTryOnEnabled(false);
                                        setPupillaryDistanceEnabled(false);
                                        // Il styleButton verrà aggiornato automaticamente dal useEffect
                                    }

                                    // Se abbiamo un provider selezionato, carica i dati del customer
                                    if (selectedProvider) {
                                        loadCustomerData(typeValue, selectedProvider);
                                    }
                                }
                            }}
                            placeholder={T._('Choose a type...', 'Admin')}
                            {...compactSelectProps}
                        />
                    </SettingControl>
                </SettingsFieldRow>
            </SettingsGroup>

            {/* Opzioni specifiche per Eyewear */}
            {isVTOEnabled && selectedType === TryOnType.Eyewear && (
                <SettingsGroup title={T._('Eyewear Options', 'Admin')}>
                    <SettingsToggleRow
                        id='settings-vto-eyewear-try-on'
                        label={T._('Virtual Try-On', 'Admin')}
                        checked={virtualTryOnEnabled}
                        onChange={(checked) => handleEyewearOptionChange('virtual_tryon', checked)}
                    />
                    <SettingsToggleRow
                        id='settings-vto-eyewear-pd'
                        label={T._('Pupillary Distance Tool', 'Admin')}
                        help={T._('Enable pupillary distance measurement tool.', 'Admin')}
                        checked={pupillaryDistanceEnabled}
                        onChange={(checked) => handleEyewearOptionChange('pupillary_distance', checked)}
                    />
                    {/* Mandatory PD - visibile solo quando il PD è abilitato */}
                    {pupillaryDistanceEnabled && (
                        <DependentOption>
                            <SettingsToggleRow
                                id='settings-vto-eyewear-pd-mandatory'
                                label={T._('Mandatory', 'Admin')}
                                help={T._('When enabled, clients will be required to use the PD measurement tool before making a purchase.', 'Admin')}
                                checked={mandatoryPDEnabled}
                                onChange={(checked) => handleMandatoryPDChange(checked)}
                            />
                        </DependentOption>
                    )}
                    <SettingsMessage>
                        {T._('Note: Disabling both options will automatically disable the Virtual Try-On feature above.', 'Admin')}
                    </SettingsMessage>
                </SettingsGroup>
            )}

            {/* Action Button Configuration - un bottone per il try-on, e per Eyewear uno per la PD */}
            {isVTOEnabled && (
                <SettingsGroup title={T._('Action Buttons', 'Admin')}>
                    <DisclosureRow
                        type='button'
                        $isOpen={isVirtualTryOnPanelOpen}
                        aria-expanded={isVirtualTryOnPanelOpen}
                        onClick={handleVirtualTryOnPanelToggle}
                    >
                        <span>{T._('Virtual Try-on Button', 'Admin')}</span>
                        <ExpandMoreIcon aria-hidden='true' />
                    </DisclosureRow>

                    {isVirtualTryOnPanelOpen && (
                        <ButtonCustomizationPanel
                            idPrefix='vto-button'
                            values={buttonCustomizations}
                            placeholder={T._('VTO', 'Admin')}
                            iconAlt={T._('Virtual Try-On icon', 'Admin')}
                            onChange={(changes) => {
                                setButtonCustomizations((prev) => ({ ...prev, ...changes }));
                                updateVirtualTryOnSettings(changes);
                            }}
                            onIconError={() => setButtonCustomizations((prev) => ({ ...prev, iconImage: '' }))}
                            onUploadIcon={handleImageUpload}
                            onResetIcon={handleResetIcon}
                            onDeleteIcon={handleDeleteIcon}
                            onResetTextLabel={handleResetTextLabel}
                        />
                    )}

                    {/* Pupillary Distance Button (solo per Eyewear) */}
                    {selectedType === TryOnType.Eyewear && pupillaryDistanceEnabled && (
                        <>
                            <DisclosureRow
                                type='button'
                                $isOpen={isPupillaryDistancePanelOpen}
                                aria-expanded={isPupillaryDistancePanelOpen}
                                onClick={handlePupillaryDistancePanelToggle}
                            >
                                <span>{T._('Pupillary Distance Button', 'Admin')}</span>
                                <ExpandMoreIcon aria-hidden='true' />
                            </DisclosureRow>

                            {isPupillaryDistancePanelOpen && (
                                <ButtonCustomizationPanel
                                    idPrefix='pd-button'
                                    values={pupillaryDistanceCustomizations}
                                    placeholder={T._('PD', 'Admin')}
                                    iconAlt={T._('Pupillary Distance icon', 'Admin')}
                                    onChange={(changes) => {
                                        setPupillaryDistanceCustomizations((prev) => ({ ...prev, ...changes }));
                                        updatePupillaryDistanceSettings(changes);
                                    }}
                                    onIconError={() => setPupillaryDistanceCustomizations((prev) => ({ ...prev, iconImage: '' }))}
                                    onUploadIcon={handlePupillaryDistanceImageUpload}
                                    onResetIcon={handlePupillaryDistanceResetIcon}
                                    onDeleteIcon={handlePupillaryDistanceDeleteIcon}
                                    onResetTextLabel={handleResetPupillaryDistanceTextLabel}
                                />
                            )}
                        </>
                    )}
                </SettingsGroup>
            )}

            {/* VTO Reference Files */}
            {isVTOEnabled && selectedType && (
                <SettingsGroup title={T._('VTO Reference Files', 'Admin')}>
                    <ReferenceStatus $tone={referenceTone} role='status'>
                        {isLoadingVTOReferences
                            ? T._('Loading reference models...', 'Admin')
                            : hasVTOReferenceModels
                              ? T._('Reference models loaded in scene', 'Admin')
                              : T._('No reference models in scene', 'Admin')}
                    </ReferenceStatus>

                    <Actions>
                        <BasicButton type='button' $size='small' onClick={() => addVTOReferenceFilesToScene(selectedType)} disabled={isLoadingVTOReferences}>
                            {isLoadingVTOReferences ? T._('Loading...', 'Admin') : T._('Load Reference Files', 'Admin')}
                        </BasicButton>
                        <BasicButton type='button' $size='small' onClick={() => downloadVTOReferenceFiles(selectedType)} disabled={isLoadingVTOReferences}>
                            {T._('Download Reference Files', 'Admin')}
                        </BasicButton>
                        {hasVTOReferenceModels && !isLoadingVTOReferences && (
                            <BasicButton
                                type='button'
                                $size='small'
                                onClick={() => {
                                    if (sceneEditor) {
                                        sceneEditor.removeAllVTOReferenceMeshes();
                                        checkForVTOReferenceFiles();
                                    }
                                }}
                            >
                                {T._('Remove Reference Files', 'Admin')}
                            </BasicButton>
                        )}
                    </Actions>

                    {/* Info sulla tipologia */}
                    <SettingsMessage>
                        {T._(`Reference files for: ${tryOnTypeOptions.find((opt) => opt.value === selectedType)?.label || 'Unknown'}`, 'Admin')}
                    </SettingsMessage>
                </SettingsGroup>
            )}
        </Container>
    );
};

// Componente principale con Suspense
interface VTOEditorProps {
    /** L'id del modello del prodotto aperto. Senza, la lettura da database non si fa. */
    productModelID?: number | string | null;
    additionalFeatures?: any;
    onSceneChanged?: () => void;
}

// IL TRY-ON SI CHIEDE PER PRODOTTO, e l'identificativo è quello con cui il salvataggio lo
// scrive (`useSaveProduct`: `toGlobalID(product.modelID, 'Product')`). È l'unico che il
// client possieda: `ProductTryOn` ha una sua identità, ma nessuna query la restituisce
// partendo dal prodotto — vedi il registro dei punti non supportati.
//
// Qui c'era un numero fisso, `1500944`. Non era un valore di comodo: la query tornava la
// configurazione try-on DI UN ALTRO PRODOTTO, e quando la scena non aveva dati VTO propri
// il pannello ne ereditava tipo e provider (righe 358 e 467). Con lo zero la query non
// trova niente, che è la risposta giusta per un prodotto che non è ancora stato salvato.
const VTOEditor: React.FC<VTOEditorProps> = ({ productModelID, additionalFeatures, onSceneChanged }) => {
    const productTryOnId = toGlobalID(productModelID || 0, 'Product');

    return (
        <Suspense fallback={<LoadingState>{T._('Loading VTO configuration...', 'Admin')}</LoadingState>}>
            <VTOEditorContent productTryOnId={productTryOnId} additionalFeatures={additionalFeatures} onSceneChanged={onSceneChanged} />
        </Suspense>
    );
};

export default VTOEditor;

// IL PROVIDER VA DETTO AL MOTORE ANCHE QUANDO QUESTA FINESTRA È CHIUSA — cioè quasi sempre. Il
// pannello VTO vive dentro le impostazioni, ma `selectedProvider` serve altrove: al pannello di una
// mesh, che da lì decide se la sezione Fine Tuning esiste, e alla vista del palco, che da lì sceglie
// la cornice. Nel vecchio backoffice il provider si impostava all'apertura della pagina, non in un
// pannello: senza, riaprendo lo scene editor il finetuning spariva finché non si passava dalle
// impostazioni.
//
// Non disegna niente: legge le due query e lo dice al motore. Lo monta lo scene editor, una volta.
const VTOProviderSyncContent: React.FC<{ productTryOnId: string }> = ({ productTryOnId }) => {
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);
    const providersQuery = useLazyLoadQuery<any>(VTO_PROVIDERS_QUERY, {});
    const productTryOnQuery = useLazyLoadQuery<any>(PRODUCT_TRY_ON_QUERY, { productTryOnId });

    React.useEffect(() => {
        if (!sceneEditor?.setProvider) return;

        const selectedType = (sceneEditor.getTryOnSelectedType?.() ?? null) as TryOnType | null;
        const isVTOEnabled = sceneEditor.getTryOnEnabled?.() ?? false;

        sceneEditor.setProvider(resolveTryOnProvider(providersQuery, productTryOnQuery, selectedType, isVTOEnabled));
    }, [sceneEditor, providersQuery, productTryOnQuery]);

    return null;
};

export const VTOProviderSync: React.FC<{ productModelID?: number }> = ({ productModelID }) => (
    <Suspense fallback={null}>
        <VTOProviderSyncContent productTryOnId={toGlobalID(productModelID || 0, 'Product')} />
    </Suspense>
);
